import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Hummer H1 (civilian Humvee, ~1995) — a gigantic, EXTREMELY WIDE, flat,
// angular military-derived off-roader at true scale (1 unit = 1 metre):
// length ~4.69, wheelbase 3.30, on enormous fat military tyres. The one and
// only identity of the H1 is that it is a WIDE, LOW, PLANTED mass: it reads at
// least as wide as it is tall. So this model is authored deliberately extreme —
// a very wide slab body, the wheels shoved right out to the EXTREME corners in
// huge bulging box flares (the flares/tyres are the widest thing on the truck),
// a dropped low stance, and a SHORT, low, flat greenhouse on top (never a tall
// upright cab). Nose is +Z. Signature cues: a PROMINENT wide vertical-slat
// grille spanning most of the flat front face, round headlamps bracketing it at
// the wide UPPER outer corners, a brush-guard hint over a wide flat black
// bumper, huge flat-topped box flares over fat military tyres, a low flat roof,
// an upright wagon tailgate with square tail lamps and a jerry-can hint,
// dark steel wheels with visible lug bolts, and black cladding / side steps.

const PAINT = "#b6a06a"; // iconic Hummer desert tan / sand — the "paint" slot default
const TRIM = "#17181b"; // gloss-black trim (pillars, grille frame, brush guard)
const CLAD = "#1e2020"; // matte black plastic (bumpers, cladding, side steps)
const FLARE = "#1c1d1f"; // matte-black squared wheel-arch flares
const WELL = "#141518"; // wheel wells
const GLASS = "#22303e"; // tinted side / rear glazing
const SCREEN = "#31465a"; // windscreen (a shade lighter than the tint)
const CHROME = "#ccd1d6";
const SLAT = "#3a3d40"; // dark grille slats
const WHEELS = "#3c3f42"; // military dark-grey steel wheels
const BOLT = "#9aa0a0"; // wheel lug bolts / hub detail
const TIRE = "#1a1b1d";
const BRAKE = "#8c9196";
const LAMP = "#e8f0f4"; // headlamp glass
const AMBER = "#e8a13a";
const RED = "#a91527";
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres), pushed for an EXTREME-width read. The truck is
// authored WIDE and LOW: the flares reach ~2.7 across while the roof is only
// ~1.66 tall, so the front silhouette is unmistakably wider than it is tall
// even after the preview camera's perspective foreshortens the width.
const WHEEL_R = 0.5; // enormous fat military tyre
const WHEEL_HW = 0.24; // half the tyre width (very fat off-road tyre, 0.48 wide)
const WHEEL_X = 1.1; // half track — wheels shoved to the EXTREME outer corners
const AXLE_F = 1.63;
const AXLE_R = -1.63; // wheelbase 3.26 (~3.30)
const BODY_HW = 1.1; // very wide slab body half width (2.20 over the panels)
const CAB_HW = 0.9; // shorter/narrower greenhouse half width (1.80), inboard of the wide body
const SILL_Y = 0.4; // tub bottom (dropped low onto the big tyres)
const BELT_Y = 1.12; // beltline / top of the wide low slab body
const ROOF_Y = 1.62; // low flat roof (kept low so width clearly wins)
const NOSE = 2.14; // front body face
const TAIL = -2.12; // rear body face

// Greenhouse shell — a short low box centred on the wide body: front just behind
// the long hood, back near the tail (a wagon roof, no open deck behind the cab).
const SHELL_ZF = 0.28;
const SHELL_ZB = -2.0;
const SHELL_TOP = 1.56; // roof underside (roof top sits at ROOF_Y)

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** One enormous fat military off-road wheel: fat tyre, brake disc, a dark steel
 *  rim recessed within the tyre outer face, eight visible lug bolts on a circle,
 *  a chrome centre cap. Every part shares `name` so the `drive` track spins them
 *  as one node. `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, 2 * WHEEL_HW, 20, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.28, 0.28, 0.03, 16, BRAKE, [x - side * 0.16, y, z], [0, 0, Math.PI / 2], { name }),
    cyl(0.34, 0.34, 0.22, 18, WHEELS, [x + side * 0.06, y, z], [0, 0, Math.PI / 2], { name }), // dark steel rim, recessed in the tyre
    cyl(0.15, 0.15, 0.06, 12, BOLT, [x + side * 0.2, y, z], [0, 0, Math.PI / 2], { name }), // hub boss
  ];
  for (let k = 0; k < 8; k++) {
    const a = (k * 2 * Math.PI) / 8;
    parts.push(
      cyl(0.032, 0.032, 0.05, 8, BOLT, [x + side * 0.21, y + 0.18 * Math.cos(a), z + 0.18 * Math.sin(a)], [0, 0, Math.PI / 2], { name }),
    );
  }
  parts.push(cyl(0.055, 0.055, 0.05, 12, CHROME, [x + side * 0.225, y, z], [0, 0, Math.PI / 2], { name })); // centre cap
  return parts;
}

/** The opened wheel well (a dark disc proud of the body side) plus the H1's
 *  huge squared, flat-topped arch flare — a very wide horizontal box over the
 *  wheel and two short vertical caps wrapping its ends. The flares are the
 *  WIDEST thing on the truck: they overlap the slab body (no gap) and bulge far
 *  out past the fat tyres, so the front / back read dramatically wide. */
function arch(side: 1 | -1, z: number): Part[] {
  const fx = side * (BODY_HW + 0.12); // flare centre; the box overlaps the body inboard and bulges to ~1.42 outboard
  return [
    cyl(0.58, 0.58, 0.05, 20, WELL, [side * (BODY_HW - 0.02), WHEEL_R, z], [0, 0, Math.PI / 2]),
    box(0.4, 0.3, 1.4, FLARE, [fx, 1.0, z]), // huge flat squared flare top, over the wheel (inner overlaps the body, outer ~1.42)
    box(0.36, 0.72, 0.22, FLARE, [fx, 0.58, z + 0.53]), // big front flare wall at the corner
    box(0.36, 0.72, 0.22, FLARE, [fx, 0.58, z - 0.53]), // big rear flare wall at the corner
  ];
}

// The six hazard indicators: round front turn signals set WIDE by the round
// headlamps, amber marker lights sitting on the front flare tops, and square
// amber rear lamps. Parts and blink tracks are generated from one table so the
// pivots (each part's own centre) can never drift from the parts.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; kind: "front" | "flare" | "rear" }> = [
  { name: "indicator-fl", pos: [-0.98, 0.66, 2.17], kind: "front" },
  { name: "indicator-fr", pos: [0.98, 0.66, 2.17], kind: "front" },
  { name: "indicator-ml", pos: [-1.28, 1.17, 1.55], kind: "flare" },
  { name: "indicator-mr", pos: [1.28, 1.17, 1.55], kind: "flare" },
  { name: "indicator-rl", pos: [-0.98, 0.66, -2.16], kind: "rear" },
  { name: "indicator-rr", pos: [0.98, 0.66, -2.16], kind: "rear" },
];

function indicatorPart(ind: (typeof INDICATORS)[number]): Part {
  switch (ind.kind) {
    case "front":
      return cyl(0.08, 0.08, 0.04, 14, AMBER, ind.pos, [Math.PI / 2, 0, 0], { name: ind.name });
    case "flare":
      return box(0.15, 0.05, 0.16, AMBER, ind.pos, undefined, { name: ind.name });
    case "rear":
      return box(0.18, 0.12, 0.06, AMBER, ind.pos, undefined, { name: ind.name });
  }
}

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.06, 0.06, 1.95, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // wide axle beam
    for (const s of S) P.push(...arch(s, z));
  }
  P.push(box(1.7, 0.2, 3.7, TRIM, [0, 0.42, 0])); // chassis / skid pan
  P.push(box(1.9, 0.16, 0.44, CLAD, [0, 0.42, 1.9])); // front apron, back to the front wells
  P.push(box(1.9, 0.16, 0.44, CLAD, [0, 0.42, -1.86])); // rear apron, back to the rear wells

  // -- Body masses (paint): one very wide, flat, LOW slab tub ------------------
  P.push(paint(2 * BODY_HW, BELT_Y - SILL_Y, NOSE - TAIL, [0, (SILL_Y + BELT_Y) / 2, (NOSE + TAIL) / 2]));
  // Long flat hood, a hair proud of the beltline, from the cowl to the nose.
  P.push(paint(1.82, 0.05, 1.36, [0, BELT_Y + 0.005, 1.44]));
  for (const s of S) P.push(paint(0.5, 0.07, 1.36, [s * 0.78, BELT_Y + 0.02, 1.44])); // raised flat fender shoulders over the front wheels

  // -- Cabin greenhouse: a SHORT, LOW box on the very wide body -----------------
  const shellCz = (SHELL_ZF + SHELL_ZB) / 2;
  const shellD = SHELL_ZF - SHELL_ZB;
  const shellCy = (BELT_Y + SHELL_TOP) / 2;
  const shellH = SHELL_TOP - BELT_Y;
  const scrCy = (BELT_Y + SHELL_TOP) / 2;
  const scrRake = 0.828; // hard windscreen rake (top pitched toward the tail)
  P.push(paint(2 * CAB_HW, shellH, shellD, [0, shellCy, shellCz])); // cabin shell, narrower than the body
  P.push(paint(2 * CAB_HW + 0.05, 0.06, shellD + 0.05, [0, ROOF_Y - 0.03, shellCz])); // low flat roof, slight lip, top at ROOF_Y
  // Hard-raked flat windscreen bridging the cowl up to the roof front, with a
  // paint frame (the A-pillars). Negative x-rotation pitches the top toward the tail.
  P.push(box(2 * CAB_HW - 0.04, 0.65, 0.05, SCREEN, [0, scrCy, 0.54], [-scrRake, 0, 0]));
  for (const s of S) P.push(paint(0.1, 0.67, 0.08, [s * (CAB_HW - 0.05), scrCy, 0.54], [-scrRake, 0, 0])); // A-pillars
  for (const s of S) P.push(box(0.34, 0.014, 0.03, TRIM, [s * 0.32, BELT_Y + 0.03, 0.75], [-scrRake, 0, 0])); // wipers on the screen
  // Side glass: a long proud band on each cabin side, split by B/C pillars.
  for (const s of S) {
    P.push(box(0.02, 0.32, 1.88, GLASS, [s * (CAB_HW + 0.008), shellCy - 0.01, shellCz])); // side glass band
    P.push(box(0.03, 0.34, 0.08, TRIM, [s * (CAB_HW + 0.014), shellCy - 0.01, -0.1])); // B-pillar
    P.push(box(0.03, 0.34, 0.08, TRIM, [s * (CAB_HW + 0.014), shellCy - 0.01, -1.1])); // C-pillar
    P.push(paint(0.04, 0.36, 0.34, [s * (CAB_HW + 0.008), shellCy - 0.01, -1.78])); // rear quarter panel
    P.push(box(0.02, 0.025, 2.0, CHROME, [s * (CAB_HW + 0.01), BELT_Y + 0.02, shellCz])); // belt-line strip
  }
  // Three small amber cab clearance lights sunk into the front roof edge.
  for (const t of [-0.26, 0, 0.26] as const) P.push(box(0.11, 0.03, 0.06, AMBER, [t, ROOF_Y - 0.01, 0.26]));
  P.push(box(0.05, 0.06, 0.16, TRIM, [0.5, ROOF_Y - 0.005, -1.7])); // stubby roof antenna base, sunk in

  // -- Front fascia: a PROMINENT wide slat grille, corner headlamps ------------
  P.push(box(1.72, 0.5, 0.04, TRIM, [0, 0.85, 2.155])); // wide, tall recessed grille panel, spanning most of the flat face
  for (let i = -6; i <= 6; i++) {
    P.push(box(0.045, 0.46, 0.03, SLAT, [i * 0.13, 0.85, 2.18])); // thirteen bold vertical grille slats
  }
  for (const s of S) {
    P.push(cyl(0.15, 0.15, 0.03, 16, TRIM, [s * 0.98, 0.98, 2.155], [Math.PI / 2, 0, 0])); // headlamp bezel, at the wide UPPER outer corner
    P.push(cyl(0.125, 0.125, 0.045, 16, LAMP, [s * 0.98, 0.98, 2.175], [Math.PI / 2, 0, 0])); // round headlamp bracketing the grille
    P.push(cyl(0.05, 0.05, 0.02, 12, CHROME, [s * 0.98, 0.98, 2.205], [Math.PI / 2, 0, 0])); // reflector
  }
  // Brush-guard hint standing proud across the wide grille.
  for (const s of S) P.push(box(0.06, 0.62, 0.06, TRIM, [s * 0.55, 0.85, 2.28])); // guard uprights
  P.push(box(1.42, 0.06, 0.06, TRIM, [0, 1.11, 2.28])); // guard top rail
  P.push(box(1.42, 0.06, 0.06, TRIM, [0, 0.6, 2.28])); // guard bottom rail
  // Wide flat black front bumper (narrower than the track), round aux lamps, plate.
  P.push(box(2.1, 0.26, 0.2, CLAD, [0, 0.42, 2.2]));
  for (const s of S) P.push(cyl(0.09, 0.09, 0.05, 14, LAMP, [s * 0.72, 0.42, 2.31], [Math.PI / 2, 0, 0])); // round aux lamp
  P.push(box(0.5, 0.14, 0.02, TRIM, [0, 0.42, 2.31])); // plate recess
  P.push(box(0.44, 0.11, 0.02, PLATE, [0, 0.42, 2.32])); // front number plate
  P.push(box(0.9, 0.1, 0.06, CLAD, [0, 0.24, 2.3])); // tow / skid hint under the bumper

  // -- Rear fascia: upright wagon tailgate, square lamps, jerry-can hint -------
  P.push(box(2 * CAB_HW - 0.06, 0.38, 0.05, GLASS, [0, shellCy - 0.01, -2.04], [0.12, 0, 0])); // near-vertical rear glass
  P.push(box(0.4, 0.03, 0.03, RED, [0, SHELL_TOP - 0.04, -2.03])); // high brake light on the tailgate top
  for (const s of S) {
    P.push(box(0.22, 0.34, 0.06, TRIM, [s * 0.98, 0.82, -2.14])); // square lamp housing
    P.push(box(0.17, 0.16, 0.06, RED, [s * 0.98, 0.9, -2.16])); // red stop / tail (amber indicator below)
  }
  // Jerry-can hint mounted on the tailgate, off to one side.
  P.push(box(0.24, 0.44, 0.14, TRIM, [0.44, 0.86, -2.2]));
  P.push(box(0.26, 0.05, 0.15, CLAD, [0.44, 1.1, -2.2])); // jerry-can top ridge / cap
  P.push(box(0.06, 0.3, 0.02, CLAD, [0.44, 0.86, -2.28])); // jerry-can face rib
  P.push(box(2.1, 0.26, 0.2, CLAD, [0, 0.42, -2.18])); // wide flat black rear bumper
  P.push(box(0.5, 0.14, 0.02, TRIM, [-0.36, 0.42, -2.29])); // plate recess
  P.push(box(0.44, 0.11, 0.02, PLATE, [-0.36, 0.42, -2.3])); // rear number plate
  for (const s of S) P.push(box(0.08, 0.06, 0.03, RED, [s * 0.58, 0.42, -2.29])); // small rear reflectors

  // -- Side furniture: black cladding, side steps, handles, mirrors -----------
  for (const s of S) {
    P.push(box(0.06, 0.14, 4.0, CLAD, [s * (BODY_HW + 0.01), 0.6, 0.01])); // lower body rub cladding, well to well
    P.push(box(0.16, 0.08, 1.8, CLAD, [s * (BODY_HW + 0.04), 0.36, -0.05])); // rock-slider side step between the axles
    P.push(box(0.03, 0.04, 0.18, CHROME, [s * (BODY_HW + 0.01), 0.86, 0.32])); // front door handle
    P.push(box(0.03, 0.04, 0.18, CHROME, [s * (BODY_HW + 0.01), 0.86, -0.7])); // rear door handle
    P.push(box(0.12, 0.03, 0.05, TRIM, [s * (BODY_HW + 0.01), 1.06, 0.9])); // mirror arm
    P.push(paint(0.09, 0.16, 0.14, [s * (BODY_HW + 0.1), 1.08, 0.86])); // big square mirror head
    P.push(box(0.07, 0.13, 0.012, CHROME, [s * (BODY_HW + 0.11), 1.08, 0.78])); // mirror glass
  }

  for (const ind of INDICATORS) P.push(indicatorPart(ind));

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const hummerH1 = defineModel({
  id: "hummer-h1",
  name: "Hummer H1",
  batchId: "cars",
  tags: ["car", "hummer", "h1", "suv", "offroad", "military", "classic"],
  summary: "A civilian Hummer H1 at true scale — extremely wide, low and boxy — in desert tan.",
  description:
    "A civilian Hummer H1 (~1995) at real 1 unit = 1 metre scale (about 4.69 " +
    "long, 3.30 wheelbase, huge fat military tyres). The H1's whole identity is " +
    "being a WIDE, LOW, planted mass — at least as wide as it is tall — so it is " +
    "authored deliberately extreme: a very wide, low, flat slab body with the " +
    "wheels shoved out to the extreme corners in huge bulging flat-topped box " +
    "flares (the flares/tyres are the widest thing on the truck), a dropped " +
    "stance, and a short low flat greenhouse with a hard-raked windscreen. All " +
    "the H1 cues: a prominent wide vertical-slat grille spanning most of the " +
    "flat front face, round headlamps bracketing it at the wide upper outer " +
    "corners, a brush-guard hint over a wide flat black bumper with round aux " +
    "lamps, huge squared black wheel-arch flares over fat military tyres, a low " +
    "flat roof with amber cab clearance lights, an upright wagon tailgate with " +
    "square tail lamps and a jerry-can hint, dark steel wheels with visible lug " +
    "bolts, black cladding and rock-slider side steps. Body panels carry the " +
    "'paint' recolour slot (desert tan); `drive` spins the four wheels and " +
    "`hazards` blinks the round front turn signals, flare-top markers and rear ambers.",
  author: "Fable 5",
  footprint: 2.5,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
